import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ActionsMenu from '../../../../components/ui/ActionsMenu';
import React, { useEffect, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import { useFetchData, useFetchFile } from '../../../../hooks';
import { MaterialRequestRecord } from '../../../../types/MaterialRequest';
import { MRS_API } from '../../../../hooks/useMaterialRequestHooks';
import { Button, Dropdown, MenuProps, Space } from 'antd';
import {
  DownloadOutlined,
  EditOutlined,
  EllipsisOutlined,
  PrinterOutlined,
} from '@ant-design/icons';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import MaterialRequestLoadingView from './MaterialRequestLoadingView';
import { cn, isUserCanEdit } from '../../../../lib/utils';
import PageHeaderAction from '../../../../components/PageHeaderAction';
import AuditCard from '../../../../components/AuditCard';
import ErrorModal from '../../../../components/ui/modal/ErrorModal';
import MaterialRequestPrintTemplate from './MaterialRequestPrintTemplate';
import MaterialRequestInfoContent from './MaterialRequestInfoContent';
import MaterialRequestInfoStatus from './MaterialRequestInfoStatus';
import { StatusHistory } from '../../../../types';
import { useAuthStore } from '../../../../store';

const MaterialRequestInfoPage = () => {
  const { user } = useAuthStore();
  const [searchParams] = useSearchParams();
  const params = useParams();
  const navigate = useNavigate();
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorMess, setErrorMess] = useState<string | undefined>('');
  const componentRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });
  const { data, isLoading, error, isError } =
    useFetchData<MaterialRequestRecord>(`${MRS_API}/${params.id}`);
  const { data: files } = useFetchFile(
    `${MRS_API}/${params.id}/files`,
    'api/stock/mrs',
  );

  const { data: statusHistory, isLoading: isLoadingHistory } = useFetchData<
    StatusHistory[]
  >(`${MRS_API}/${params.id}/status-logs`);

  const exportToPDF = () => {
    html2canvas(componentRef.current as HTMLDivElement).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('l', 'mm', 'a4');
      const componentWidth = pdf.internal.pageSize.getWidth();
      const componentHeight = pdf.internal.pageSize.getHeight();
      pdf.addImage(imgData, 'PNG', 0, 0, componentWidth, componentHeight);
      pdf.save(`material_request_${data?.code}.pdf`);
    });
  };

  const isCurrentUserCanEdit = () => {
    return (
      user?.role === 'ROLE_ADMIN' ||
      isUserCanEdit(user?.moduleAccess, 'MaterialRequest')
    );
  };

  const items: MenuProps['items'] = [
    {
      label: 'Edit',
      key: 'edit',
      icon: <EditOutlined />,
      disabled:
        !isCurrentUserCanEdit() ||
        data?.status === 'ISSUED' ||
        data?.status === 'APPROVED' ||
        data?.status === 'READY' ||
        data?.status === 'DONE' ||
        data?.status === 'CANCEL',
    },
    {
      label: 'Download',
      key: 'download',
      icon: <DownloadOutlined />,
      onClick: exportToPDF,
    },
    {
      label: 'Print',
      key: 'print',
      icon: <PrinterOutlined />,
      onClick: handlePrint, // Trigger print action directly when clicked
    },
  ];

  const onClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'edit') {
      navigate('/stock-movement/material-request/request/' + data?.id);
    }
  };

  const handleErrorModalClose = () => {
    setIsErrorModalOpen(false);
  };
  const handleClose = () => {
    navigate('/stock-movement/material-request');
  };

  useEffect(() => {
    if (isError) {
      setIsErrorModalOpen(isError);
      setErrorMess(error?.response?.data.message);
    }
  }, [isError]);

  if (isLoading && isLoadingHistory) {
    return <MaterialRequestLoadingView active={true} />;
  }

  return (
    <>
      <div
        className={cn(
          'flex items-center justify-between',
          isLoading ? 'hidden' : '',
        )}
      >
        <PageHeaderAction
          action={searchParams.get('action')}
          moduleName={'Material Request'}
        />
        <ActionsMenu items={items} onClick={onClick} onBack={handleClose} />
      </div>
      <div className={'flex'}>
        <MaterialRequestInfoContent
              data={data}
              attachments={files}
              api={MRS_API}
              recordId={params.id}
              canEdit={isUserCanEdit(user?.moduleAccess, 'MaterialRequest')}
            />
        <div className={'w-3/12'}>
          <MaterialRequestInfoStatus
            data={data}
            statusHistory={statusHistory !== undefined ? statusHistory : []}
          />
          <div className={'m-4 space-y-4'}>
            <AuditCard
              createdBy={data?.createdBy}
              updatedBy={data?.updatedBy}
              timeCreated={data?.timeCreated}
              timeUpdated={data?.timeUpdated}
            />
          </div>
        </div>
      </div>
      <ErrorModal
        visible={isErrorModalOpen}
        message={errorMess}
        onClose={handleErrorModalClose}
      />

      <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
        <MaterialRequestPrintTemplate data={data} ref={componentRef} />
      </div>
    </>
  );
};

export default MaterialRequestInfoPage;
